import * as React from "react";

/**
 * Close an overlay on an outside pointer press, listening in the CAPTURE phase
 * at the document level.
 *
 * Radix's own outside-press dismissal listens on `document` in the BUBBLE
 * phase, but the reader's gesture hook (use-viewport-gestures)
 * `stopPropagation()`s claimed touch/pen pointerdowns in
 * the CAPTURE phase on the document scroller — so the event never bubbles and
 * Radix never dismisses on tablets. A capture listener on `document` fires
 * BEFORE any descendant capture listener (including the scroller's), so it is
 * not starved and works for touch/pen as well as mouse.
 *
 * Escape is left to Radix, which already handles it.
 */
export function useOutsidePressCapture(
  getContentEl: () => HTMLElement | null,
  getTriggerEl: () => HTMLElement | null,
  onOutside: () => void,
  active: boolean,
) {
  // Keep the latest getters/callback in a ref so we only (re)subscribe when
  // `active` flips — not on every render of the consumer.
  const latest = React.useRef({ getContentEl, getTriggerEl, onOutside });
  latest.current = { getContentEl, getTriggerEl, onOutside };

  React.useEffect(() => {
    if (!active) return;
    const handle = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      const { getContentEl: content, getTriggerEl: trigger, onOutside: close } = latest.current;
      if (content()?.contains(target)) return;
      if (trigger()?.contains(target)) return;
      close();
    };
    document.addEventListener("pointerdown", handle, true);
    return () => document.removeEventListener("pointerdown", handle, true);
  }, [active]);
}
